import { useEffect } from 'react';
import { ArrowCircleUp, X } from '@phosphor-icons/react';
import { motion, useReducedMotion, type Transition } from 'motion/react';

import heruLogo from '@/assets/img/svg/heru-logo-v3.svg';
import { Button } from '@/components/ui/button';
import useHideMobileMenu from '@/hooks/useHideMobileMenu';
import { initSessionRecording } from '@/shared/analytics/sessionRecording';
import { EVENT_TYPES } from '@/shared/constants/events';
import { type AppUpdatePrompt } from '../lib/appUpdateDecision';
import { openStore } from '../lib/openStore';
import { trackAppUpdate } from '../lib/trackAppUpdate';

const EASE = [0.22, 1, 0.36, 1] as const;
const RISE_FROM = { opacity: 0, y: 12 };
const FADE_FROM = { opacity: 0 };
const SETTLED = { opacity: 1, y: 0 };
const QUICK_FADE: Transition = { duration: 0.2 };

const TILE_ENTRANCE: Transition = {
	opacity: { duration: 0.25, delay: 0.1 },
	scale: { type: 'spring', stiffness: 260, damping: 24, delay: 0.1 },
};

const updatePulse = (duration: number, ease: 'easeOut' | 'easeInOut'): Transition => ({
	duration,
	ease,
	delay: 0.75,
	repeat: 2,
	repeatDelay: 3.6 - duration,
});

/** Full-screen update view: without `onClose` there is no way out; with it, a close button. */
export const AppUpdateScreen = ({
	prompt,
	onClose,
}: {
	prompt: AppUpdatePrompt;
	onClose?: () => void;
}) => {
	useHideMobileMenu();
	const closable = Boolean(onClose);
	const still = Boolean(useReducedMotion());

	useEffect(() => {
		if (!closable) initSessionRecording();
		trackAppUpdate(EVENT_TYPES.APP_UPDATE_PROMPT_SHOWN, prompt);
	}, [prompt, closable]);

	const handleUpdateClick = () => {
		trackAppUpdate(EVENT_TYPES.APP_UPDATE_STORE_OPENED, prompt);
		openStore(prompt.storeUrl);
	};

	const handleCloseClick = () => {
		trackAppUpdate(EVENT_TYPES.APP_UPDATE_PROMPT_DISMISSED, prompt);
		onClose?.();
	};

	const enterFrom = still ? FADE_FROM : RISE_FROM;
	const enter = (delay: number): Transition => (still ? QUICK_FADE : { duration: 0.45, ease: EASE, delay });

	return (
		<main
			role='alertdialog'
			aria-modal='true'
			aria-labelledby='app-update-title'
			className='bg-h-primary-95 fixed inset-0 z-[2000] flex min-h-dvh flex-col overflow-y-auto px-6 pt-[max(2.5rem,env(safe-area-inset-top))] pb-[max(2.5rem,env(safe-area-inset-bottom))] text-center'>
			{onClose && (
				<button
					type='button'
					aria-label='Cerrar'
					onClick={handleCloseClick}
					style={{ boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px' }}
					className='absolute top-[max(0.75rem,env(safe-area-inset-top))] right-3 z-30 cursor-pointer rounded-full bg-white p-2 text-black'>
					<X size={18} weight='bold' />
				</button>
			)}
			<div className='my-auto flex flex-col items-center'>
				<motion.img
					src={heruLogo}
					alt='heru'
					initial={enterFrom}
					animate={SETTLED}
					transition={enter(0)}
					className='mb-10 h-7 w-auto object-contain'
				/>
				<motion.span
					aria-hidden
					initial={still ? FADE_FROM : { opacity: 0, scale: 0.7 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={still ? QUICK_FADE : TILE_ENTRANCE}
					className='relative grid size-16 place-items-center'>
					{!still && (
						<motion.span
							initial={FADE_FROM}
							animate={{ opacity: [0.5, 0], scale: [1, 1.5] }}
							transition={updatePulse(1.8, 'easeOut')}
							className='bg-h-primary-80 absolute inset-0 rounded-2xl'
						/>
					)}
					<span className='bg-h-primary-90 relative grid size-16 place-items-center rounded-2xl'>
						<motion.span
							animate={still ? undefined : { y: [0, -4, 0] }}
							transition={updatePulse(1.2, 'easeInOut')}
							className='grid'>
							<ArrowCircleUp size={32} weight='fill' className='text-h-primary-50' />
						</motion.span>
					</span>
				</motion.span>
				<motion.h1
					id='app-update-title'
					initial={enterFrom}
					animate={SETTLED}
					transition={enter(0.2)}
					className='text-h-text-heading mt-6 text-2xl font-bold md:text-3xl'>
					¡Hay una nueva versión disponible!
				</motion.h1>
				<motion.p
					initial={enterFrom}
					animate={SETTLED}
					transition={enter(0.28)}
					className='text-h-text-medium mt-3 max-w-md text-base leading-relaxed'>
					Te invitamos a actualizar la app para que tengas la mejor experiencia.
				</motion.p>
				<motion.div
					initial={enterFrom}
					animate={SETTLED}
					transition={enter(0.36)}
					className='mt-8 w-full max-w-xs'>
					<Button
						type='button'
						haptic
						onClick={handleUpdateClick}
						className='bg-h-primary-50 h-12 w-full rounded-full text-white'>
						Actualizar
					</Button>
				</motion.div>
			</div>
		</main>
	);
};
